import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger, SpecraButton, SpecraDivider, SpecraIconButton } from '@mono/ui-components';
import { Check, ChevronDown, FolderOpen, LogOut, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProjectTypeStyles } from '../constants/project-types';
import { useFeatureFlag, useFeatureFlagsReady } from '../contexts/featureFlagContext';
import { useProjects } from '../hooks/useProjects';
import { proposalHref } from '../lib/proposalHref';
import { cn } from '../lib/utils';
/**
 * Project selector dropdown for switching between projects.
 * Shows current project, recent projects, and options to exit project mode.
 */ export function ProjectSelector({ isCollapsed, currentProjectId }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const flagsReady = useFeatureFlagsReady();
    const specraRedesignEnabled = useFeatureFlag('specra_redesign_dashboard');
    const specraRedesign = flagsReady && specraRedesignEnabled;
    // Use the passed projectId (works for both /project/:projectId and /proposal/:id routes)
    const projectId = currentProjectId;
    // Fetch recent projects
    const { data: projectsData, isLoading } = useProjects({
        status: 'active'
    });
    const projects = projectsData?.projects ?? [];
    // Find current project
    const currentProject = projects.find((p)=>p.projectId === projectId);
    const handleSelectProject = (project)=>{
        setOpen(false);
        if (project.type === 'proposal') {
            navigate(proposalHref(project.projectId));
        } else {
            navigate(`/project/${project.projectId}`);
        }
    };
    const handleExitProject = ()=>{
        setOpen(false);
        navigate('/dashboard');
    };
    if (isCollapsed) {
        return /*#__PURE__*/ _jsx("div", {
            className: "px-2 py-1",
            children: /*#__PURE__*/ _jsx(Link, {
                to: "/projects",
                title: "Projects",
                children: /*#__PURE__*/ _jsx(SpecraIconButton, {
                    variant: "secondary",
                    label: "Projects",
                    className: "w-full h-10",
                    children: /*#__PURE__*/ _jsx(FolderOpen, {
                        className: "w-4 h-4"
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: specraRedesign ? '' : 'px-2 py-1',
        children: /*#__PURE__*/ _jsxs(Popover, {
            open: open,
            onOpenChange: setOpen,
            children: [
                /*#__PURE__*/ _jsx(PopoverTrigger, {
                    asChild: true,
                    children: specraRedesign ? /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "aria-haspopup": "listbox",
                        "aria-expanded": open,
                        className: "flex min-w-0 max-w-full items-center gap-2 rounded-md bg-card px-2.5 py-1 text-sm shadow-sm transition-colors hover:shadow",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate font-medium text-foreground",
                                children: currentProject?.name ?? 'Select project...'
                            }),
                            /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "w-3.5 h-3.5 flex-shrink-0 text-muted-foreground"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "secondary",
                        "aria-haspopup": "listbox",
                        "aria-expanded": open,
                        className: "w-full justify-between font-normal",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 truncate",
                                children: [
                                    /*#__PURE__*/ _jsx(FolderOpen, {
                                        className: "w-4 h-4 flex-shrink-0"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "truncate",
                                        children: currentProject?.name ?? 'Select project...'
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "w-4 h-4 flex-shrink-0 opacity-50"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs(PopoverContent, {
                    className: "w-64 p-0",
                    align: "start",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "p-2",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "px-2 py-1.5 text-xs font-medium text-muted-foreground",
                                    children: "Recent Projects"
                                }),
                                isLoading ? /*#__PURE__*/ _jsx("div", {
                                    className: "py-4 text-center text-sm text-muted-foreground",
                                    "aria-busy": "true",
                                    children: "Loading..."
                                }) : projects.length === 0 ? /*#__PURE__*/ _jsx("div", {
                                    className: "py-4 text-center text-sm text-muted-foreground",
                                    children: "No projects yet"
                                }) : /*#__PURE__*/ _jsx("div", {
                                    className: "max-h-64 overflow-y-auto",
                                    children: projects.slice(0, 10).map((project)=>{
                                        const styles = getProjectTypeStyles(project.type);
                                        return /*#__PURE__*/ _jsxs("button", {
                                            onClick: ()=>handleSelectProject(project),
                                            "aria-pressed": project.projectId === projectId,
                                            className: cn('w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-muted transition-colors text-left', project.projectId === projectId && 'bg-muted'),
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: cn('w-1 h-4 rounded-full', styles.bar)
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "truncate flex-1",
                                                    children: project.name
                                                }),
                                                project.projectId === projectId && /*#__PURE__*/ _jsx(Check, {
                                                    className: "w-4 h-4 flex-shrink-0"
                                                })
                                            ]
                                        }, project.projectId);
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraDivider, {}),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "p-2",
                            children: [
                                /*#__PURE__*/ _jsx(Link, {
                                    to: "/projects",
                                    onClick: ()=>setOpen(false),
                                    children: /*#__PURE__*/ _jsxs("button", {
                                        className: "w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-muted transition-colors text-left",
                                        "aria-label": "View all projects",
                                        children: [
                                            /*#__PURE__*/ _jsx(Plus, {
                                                className: "w-4 h-4 flex-shrink-0 text-muted-foreground"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "All Projects"
                                            })
                                        ]
                                    })
                                }),
                                projectId && /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleExitProject,
                                    "aria-label": "Exit current project",
                                    className: "w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-muted transition-colors text-left text-muted-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx(LogOut, {
                                            className: "w-4 h-4 flex-shrink-0"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Exit Project"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
